import { React, useContext, useEffect, useState, useId } from "react";
import { AppContext } from "../App";
import { callApi, setCookie, getInlineLoader } from "../Helpers";
import { FiCheckCircle, FiClock, FiArrowRight, FiFileText, FiMail, FiLock, FiAlertCircle, FiBriefcase, FiMapPin } from "react-icons/fi";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);
  const scopeId = useId().replace(/:/g, "");

  /* Component States */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  
  const [isApplying, setIsApplying] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [showOtpPrompt, setShowOtpPrompt] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");

  async function init() {
    setReady(false);
    await Promise.all([
      appContext?.jobApplicantAuthCheck?.(),
      (async () => {
        if (uuid) {
          await getData();
        }
      })(),
    ]);
    setReady(true);
  }

  async function getData() {
    await callApi('jobapplicant/get-job-data', { jobUuid: uuid }).then((response) => {
      if (response && response.status === 1) {
        setData(response.data);
      } else {
        appContext?.tellError?.('Could not fetch data');
      }
    });
  }

  async function handleApplyInitiation() {
    setIsApplying(true);
    const isAuthenticated = appContext?.auth;
    setIsApplying(false);

    if (isAuthenticated) {
      sendApplication();
    } else {
      setShowEmailPrompt(true);
      setShowOtpPrompt(false);
    }
  }

  async function handleEmailSubmit() {
    if (!emailInput || !emailInput.includes('@')) {
      appContext?.tellError?.("Please enter a valid email address.");
      return;
    }

    setIsApplying(true);
    await callApi('jobapplicant/access-account', { email: emailInput }).then(response => {
      if (response && response.status === 1) {
        appContext?.tellMessage?.(response.msg);
        setShowOtpPrompt(true); 
      } else {
        appContext?.tellError?.(response.msg || "Failed to process request.");
      }
    }).catch(() => {
      appContext?.tellError?.("Network error occurred.");
    });
    setIsApplying(false);
  }

  async function handleOtpSubmit() {
    if (!otpInput) {
      appContext?.tellError?.("Please enter the OTP.");
      return;
    }

    setIsApplying(true);
    await callApi('jobapplicant/verify-otp', { email: emailInput, otp: otpInput }).then(response => {
      if (response && response.status === 1) {
        appContext?.tellMessage?.(response.msg);
        setCookie('userUuid', response.data.uuid, 30);
        setCookie('userToken', response.data.token, 30);
        resetPrompts();
        sendApplication();
      } else {
        appContext?.tellError?.(response.msg || "Invalid OTP.");
      }
    }).catch(() => {
      appContext?.tellError?.("Network error occurred.");
    });
    setIsApplying(false);
  }

  function resetPrompts() {
    setShowEmailPrompt(false);
    setShowOtpPrompt(false);
    setEmailInput("");
    setOtpInput("");
  }

  async function sendApplication() {
    setIsApplying(true);
    await callApi('jobapplicant/get-current-applicant-data', {}).then((response) => {
      if (response && response.status === 1) {
        const applicant = response.data;
        setProfile(applicant);

        const isIncomplete = 
          !applicant.firstName?.trim() ||
          !applicant.middleName?.trim() ||
          !applicant.lastName?.trim() ||
          !applicant.phone?.trim() ||
          !applicant.physicalAddress?.trim() ||
          !applicant.dob?.trim() ||
          !applicant.gender?.trim();

        if (isIncomplete) {
          appContext?.tellError?.("Please complete your profile details before submitting an application.");
          appContext?.navTo?.({
            item: 'side-view',
            subItem: 'edit-applicant-profile'
          });
        } else {
          appContext?.navTo?.({
            item: 'side-view',
            subItem: 'create-application', 
            extraItem: data.uuid
          });
        }
      } else {
        appContext.tellError(response.msg);
      }
    }).catch(() => {
      appContext?.tellError?.("Network connection error verification loop.");
    });
    setIsApplying(false);
  }

  useEffect(() => {
    init();
  }, [uuid]);

  useEffect(() => {
    appContext?.setShowOverlayLoader?.(loading);
    return () => {
      appContext?.setShowOverlayLoader?.(false);
    }
  }, [loading]);

  if (ready && data) {
    return (
      <>
        <style>{`
          .${scopeId}-drawer-view {
            background-color: #091118;
            color: #f3f4f6;
            min-height: 100%;
            font-family: system-ui, -apple-system, sans-serif;
            padding: 24px;
          }

          /* --- Compact Header Area --- */
          .${scopeId}-header-block {
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            padding-bottom: 20px;
            margin-bottom: 24px;
          }

          .${scopeId}-badge-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
          }

          .${scopeId}-status-chip {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 4px 10px;
            border-radius: 4px;
            font-weight: 700;
          }

          .${scopeId}-chip-published {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
            border: 1px solid rgba(16, 185, 129, 0.15);
          }

          .${scopeId}-chip-default {
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;
            border: 1px solid rgba(245, 158, 11, 0.15);
          }

          .${scopeId}-main-title {
            font-size: 1.5rem;
            font-weight: 800;
            line-height: 1.25;
            color: #ffffff;
            margin: 0 0 14px 0;
            letter-spacing: -0.02em;
          }

          .${scopeId}-meta-flex {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            font-size: 0.8rem;
            color: rgba(255, 255, 255, 0.5);
          }

          .${scopeId}-meta-item {
            display: flex;
            align-items: center;
            gap: 6px;
          }

          /* --- Interactive Action Panel --- */
          .${scopeId}-action-wrapper {
            margin-bottom: 24px;
          }

          .${scopeId}-btn-trigger {
            width: 100%;
            padding: 14px;
            font-size: 0.8rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            transition: all 0.2s ease;
            background: var(--text-primary, #ffffff);
            color: #050b11;
          }

          .${scopeId}-btn-trigger:hover:not(:disabled) {
            filter: brightness(1.1);
            transform: translateY(-1px);
          }

          .${scopeId}-gate-box {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.06);
            border-radius: 4px;
            padding: 18px;
          }

          .${scopeId}-gate-title {
            font-size: 0.8rem;
            font-weight: 700;
            color: #ffffff;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .${scopeId}-input-field-group {
            position: relative;
            margin-bottom: 12px;
          }

          .${scopeId}-input-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: rgba(255, 255, 255, 0.4);
          }

          .${scopeId}-input-element {
            width: 100%;
            background: #050b11;
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 4px;
            padding: 11px 11px 11px 36px;
            color: #ffffff;
            font-size: 0.85rem;
            outline: none;
          }

          .${scopeId}-input-element:focus {
            border-color: var(--secondaryColor, #ffffff);
          }

          .${scopeId}-grid-actions {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 8px;
          }

          .${scopeId}-btn-abort {
            background: transparent;
            border: 1px solid rgba(255, 255, 255, 0.15);
            color: #ffffff;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 700;
            cursor: pointer;
            text-transform: uppercase;
          }

          .${scopeId}-btn-abort:hover {
            background: rgba(255, 255, 255, 0.04);
          }

          /* --- Description Render --- */
          .${scopeId}-description-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 0.75rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: var(--secondaryColor, #fff);
            margin-bottom: 14px;
          }

          .${scopeId}-rich-text {
            font-size: 0.95rem;
            line-height: 1.65;
            color: rgba(255, 255, 255, 0.8);
          }

          .${scopeId}-rich-text p { margin-bottom: 16px; }
          .${scopeId}-rich-text ul, .${scopeId}-rich-text ol { margin: 0 0 20px 16px; }
          .${scopeId}-rich-text li { margin-bottom: 6px; }
        `}</style>

        <div className={`${scopeId}-drawer-view`}>
          
          {/* Header Node */}
          <header className={`${scopeId}-header-block`}>
            <div className={`${scopeId}-badge-row`}>
              <span className={`${scopeId}-status-chip ${data.status === 'published' ? `${scopeId}-chip-published` : `${scopeId}-chip-default`}`}>
                {data.status === 'published' ? <FiCheckCircle size={11} /> : <FiClock size={11} />}
                {data.status}
              </span>
            </div>

            <h1 className={`${scopeId}-main-title`}>{data.title}</h1>

          </header>

          {/* Core Interactive Action Panel */}
          <section className={`${scopeId}-action-wrapper`}>
            {!showEmailPrompt && !showOtpPrompt ? (
              <button
                className={`${scopeId}-btn-trigger`}
                onClick={handleApplyInitiation}
                disabled={isApplying}
              >
                <span>{isApplying ? "Checking Instance..." : "Apply to Position"}</span>
                {!isApplying && <FiArrowRight size={14} />}
              </button>
            ) : showOtpPrompt ? (
              <div className={`${scopeId}-gate-box`}>
                <div className={`${scopeId}-gate-title`}>
                  <FiLock size={12} style={{ color: '#10b981' }} />
                  <span>Verify Verification Token</span>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', margin: '0 0 12px 0', lineHeight: '1.4' }}>
                  Dynamic key sent to {emailInput}.
                </p>
                <div className={`${scopeId}-input-field-group`}>
                  <FiAlertCircle className={`${scopeId}-input-icon`} size={14} />
                  <input 
                    type="text"
                    className={`${scopeId}-input-element`}
                    placeholder="6-digit token..."
                    value={otpInput}
                    onChange={(e) => setOtpInput(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className={`${scopeId}-grid-actions`}>
                  <button 
                    className={`${scopeId}-btn-trigger`}
                    onClick={handleOtpSubmit}
                    disabled={isApplying}
                  >
                    {isApplying ? "Validating..." : "Confirm"}
                  </button>
                  <button className={`${scopeId}-btn-abort`} onClick={resetPrompts}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className={`${scopeId}-gate-box`}>
                <div className={`${scopeId}-gate-title`}>
                  <FiMail size={12} style={{ color: 'var(--secondaryColor)' }} />
                  <span>Identity Validation</span>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', margin: '0 0 12px 0', lineHeight: '1.4' }}>
                  Enter your address to log or cross-reference your submission profile.
                </p>
                <div className={`${scopeId}-input-field-group`}>
                  <FiMail className={`${scopeId}-input-icon`} size={14} />
                  <input 
                    type="email"
                    className={`${scopeId}-input-element`}
                    placeholder="name@company.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className={`${scopeId}-grid-actions`}>
                  <button 
                    className={`${scopeId}-btn-trigger`}
                    onClick={handleEmailSubmit}
                    disabled={isApplying}
                  >
                    {isApplying ? "Routing..." : "Next"}
                  </button>
                  <button className={`${scopeId}-btn-abort`} onClick={resetPrompts}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Description Node */}
          <main>
            <div className={`${scopeId}-description-title`}>
              <FiFileText size={14} />
              <span>Job Specifications</span>
            </div>
            <div 
              className={`${scopeId}-rich-text`}
              dangerouslySetInnerHTML={{ __html: data.description }}
            ></div>
          </main>

        </div>
      </>
    );
  } else {
    return (
      <div style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {getInlineLoader()}
      </div>
    );
  }
}